/**
 * NameSyncBadge -- does the switch hold this port's name yet? -- and NamesNotWrittenCallout.
 *
 * Meridian is the source of truth for port names (owner decision 2026-09-14, option C): a name is
 * saved in Meridian and the controller writes it to the switch description in the background --
 * automatically only on switches assigned to a site. Imported (pre-auto-sync) names and names on
 * unassigned switches are written only by the explicit msp_admin action "Write names to switch".
 * Mapping and wording: lib/portNameSync.
 *
 * DESIGN
 *   * Status ramp for the operational states, at a lower weight than a device StatusPill: this
 *     sits beside a name in a dense table, so it is a 10px chip, and the steady state (Synced) is
 *     text-only with no ground -- a table of 28 synced ports stays calm and the exceptions are what
 *     the eye lands on.
 *   * "Not written to the switch yet" is NOT on the ramp: nothing was attempted, which is neither
 *     healthy nor a fault. Neutral ground + hollow dot, the same encoding as a never-seen device.
 *   * Syncing carries a pulsing dot (motion-safe only): "in progress" in a second channel besides
 *     colour, and nothing moves for someone who asked for reduced motion.
 *   * The action (Retry / Write names to switch) is brand -- an interactive affordance, never a
 *     status colour -- and msp_admin only. Every other role sees the badge and the reason only.
 *   * `compact` (the table) keeps the reason truncated with the full text on hover; the device
 *     sheet shows it in full.
 */
import { useRetryNameSync, useWriteNames } from "@/lib/api/hooks";
import { Button } from "@/components/ui/Button";
import { nameSyncBadge, namesNotWritten, type NameBadge, type PortNameSync } from "@/lib/portNameSync";
import { useSession } from "@/stores/session";
import { cn } from "@/lib/cn";

/** Chip treatment per tone. Synced has no ground (steady state stays quiet). */
const CHIP: Record<NameBadge["tone"], string> = {
  neutral: "bg-surface-2 text-ink-muted px-1.5",
  healthy: "text-healthy",
  info: "bg-info-soft text-info px-1.5",
  warn: "bg-warn-soft text-warn px-1.5",
  critical: "bg-critical-soft text-critical px-1.5",
};

/** Error text of a failed action request (the API's plain `message` when it sent one). */
function actionError(e: unknown): string {
  const body = (e as { body?: { message?: string } } | null)?.body;
  if (body?.message) return body.message;
  return e instanceof Error ? e.message : "The request failed.";
}

export function NameSyncBadge({
  serial,
  sync,
  compact = false,
}: {
  serial: string;
  sync: PortNameSync | null | undefined;
  /** Table cell: reason truncated (full text on hover). Device sheet: reason in full. */
  compact?: boolean;
}) {
  const badge = nameSyncBadge(sync);
  const canWrite = useSession((s) => s.hasRole("msp_admin"));
  const retry = useRetryNameSync(serial);
  const write = useWriteNames(serial);
  if (!badge) return null;

  const action = badge.action === "retry" ? retry : badge.action === "write" ? write : null;
  const showReason = badge.kind === "waiting" || badge.kind === "failed" || (badge.kind === "imported" && !compact);
  return (
    <span className={cn("flex min-w-0 gap-x-2 gap-y-0.5", compact ? "flex-wrap items-center" : "flex-col items-start")}>
      <span className="flex min-w-0 flex-wrap items-center gap-2">
        <span
          className={cn(
            "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full py-0.5 text-[10px] font-medium leading-none",
            CHIP[badge.tone],
          )}
          data-status={badge.tone === "neutral" ? undefined : badge.tone}
          title={badge.detail ?? undefined}
          aria-label={`Name sync: ${badge.label}${badge.detail ? ` — ${badge.detail}` : ""}`}
        >
          <span
            aria-hidden
            className={cn(
              "inline-block h-1.5 w-1.5 rounded-full",
              badge.tone === "neutral" ? "border border-current bg-transparent" : "bg-current",
              badge.kind === "syncing" && "motion-safe:animate-pulse",
            )}
          />
          {badge.label}
        </span>
        {action && canWrite && (
          <button
            type="button"
            className="shrink-0 rounded px-1 text-[11px] font-medium text-brand hover:underline disabled:opacity-50"
            disabled={action.isPending}
            onClick={() => action.mutate()}
            title={badge.action === "write" ? "Write every name on this switch that is not on it yet" : undefined}
          >
            {action.isPending ? "Sending…" : badge.action === "retry" ? "Retry" : "Write names to switch"}
          </button>
        )}
      </span>
      {showReason && badge.detail && (
        <span
          className={cn(
            "min-w-0 text-[11px] leading-snug",
            badge.kind === "failed" ? "text-critical" : "text-ink-muted",
            compact ? "max-w-[16rem] truncate" : "break-words",
          )}
          title={compact ? badge.detail : undefined}
        >
          {badge.detail}
        </span>
      )}
      {action?.error && <span className="text-[11px] text-critical">{actionError(action.error)}</span>}
    </span>
  );
}

/**
 * Device sheet callout: names on this switch that Meridian will NOT write on its own, with the one
 * explicit way to send them. Renders nothing when there are none. Everyone sees the count and why;
 * only msp_admin gets the button.
 */
export function NamesNotWrittenCallout({
  serial,
  ports,
}: {
  serial: string;
  ports: readonly { name_sync?: PortNameSync | null }[];
}) {
  const counts = namesNotWritten(ports);
  const canWrite = useSession((s) => s.hasRole("msp_admin"));
  const write = useWriteNames(serial);
  if (!counts.total) return null;
  const plural = counts.total === 1 ? "" : "s";
  return (
    <div className="rounded-md border border-line bg-surface-2 p-3" role="status">
      <div className="text-sm font-medium text-ink">
        <span className="tabular-nums">{counts.total}</span> port name{plural} not written to this switch
      </div>
      {counts.imported > 0 && (
        <p className="mt-1 text-xs text-ink-muted">
          {counts.imported === 1 ? "One was" : `${counts.imported} were`} saved before names synced automatically and may be
          out of date. Clear any that are wrong, or write them to the switch.
        </p>
      )}
      {counts.unassigned > 0 && (
        <p className="mt-1 text-xs text-ink-muted">
          This switch isn't assigned to a site, so its names are not written automatically. Assigning it to a site
          sends them.
        </p>
      )}
      {canWrite && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Button variant="secondary" disabled={write.isPending} onClick={() => write.mutate()}>
            {write.isPending ? "Sending…" : "Write names to switch"}
          </Button>
          {write.isSuccess && <span className="text-xs text-ink-muted">Queued — writing to the switch now.</span>}
          {write.error && <span className="text-xs text-critical">{actionError(write.error)}</span>}
        </div>
      )}
    </div>
  );
}
